import type { PromptVariable } from '@/domain/prompt.types'
import { PROMPT_VARIABLE_TYPE_LABEL, PromptVariableType, placeholdersIn } from '@/domain/prompt.types'

/** What a join puts between its parts when it declares no `separator` of its own. */
const DEFAULT_SEPARATOR = ', '

/**
 * What the body's placeholders actually expand to.
 *
 * The problem this solves: a body of `{{scene}}` tells an author nothing, while the
 * six variables that feed it sit in a panel looking unused. The composition is the
 * missing explanation, and it cannot be read off either surface alone — the body
 * names one key, the variable list names the parts, and only `joins` connects them.
 *
 * Drawn with connector lines rather than indentation alone. Indentation on its own
 * left the depth ambiguous once a join nested inside a join: `{{subject}}` two levels
 * down looked much like `{{action}}` one level down, and those compose into different
 * phrases. The rules make each parent's span explicit.
 *
 * SEPARATORS are shown because they are load-bearing and invisible otherwise: this
 * prompt joins a subject to its brief with a SPACE and the scene qualifiers with a
 * COMMA, which is the whole reason two nested joins exist rather than one.
 *
 * A tree rather than a rendered sample, because a sample needs values and an author
 * editing the template has none yet.
 */
export function PromptCompositionMap({ body, variables }: { body: string; variables: PromptVariable[] }) {
	const byKey = new Map(variables.map((v) => [v.key, v]))
	const composed = placeholdersIn(body).filter((key) => byKey.get(key)?.type === PromptVariableType.JOIN)

	// Nothing to explain when the body names no composed variable: every placeholder
	// already reads as the thing it is.
	if (composed.length === 0) return null

	return (
		<section aria-label="Composition" className="mt-3 rounded-ds-md border border-ds-border bg-ds-surface p-3">
			<h4 className="mb-2 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">What the placeholders expand to</h4>
			{composed.map((key) => (
				<Node key={key} nodeKey={key} byKey={byKey} seen={new Set()} isLast />
			))}
		</section>
	)
}

/**
 * One variable in the tree, with its parts nested beneath it.
 *
 * `seen` guards a cycle: the save-time schema check refuses one, but a body being
 * edited is not yet saved, so this renders against variables that may briefly
 * reference each other.
 */
function Node({
	nodeKey,
	byKey,
	seen,
	isLast,
	separator,
}: {
	nodeKey: string
	byKey: Map<string, PromptVariable>
	seen: Set<string>
	/** Last child of its parent — the connector stops rather than continuing down. */
	isLast: boolean
	/** What the PARENT inserts before this part; absent on a first child. */
	separator?: string
}) {
	const variable = byKey.get(nodeKey)
	const isJoin = variable?.type === PromptVariableType.JOIN
	const parts = variable?.joins ?? []
	const isCycle = seen.has(nodeKey)
	const showChildren = isJoin && !isCycle && parts.length > 0

	return (
		<div>
			<div className="flex items-baseline gap-2">
				{/* One text node, so the placeholder reads and copies as a unit. */}
				<span className="font-mono text-ds-2xs text-ds-text">{`{{${nodeKey}}}`}</span>

				{variable === undefined ? (
					<span className="text-ds-2xs text-ds-danger">not declared</span>
				) : isCycle ? (
					<span className="text-ds-2xs text-ds-warning">already above — a cycle</span>
				) : (
					<span className="text-ds-2xs text-ds-text-subtle">
						{isJoin
							? parts.length === 0
								? 'composes nothing — renders empty'
								: `joins ${parts.length}, separated by ${describeSeparator(variable.separator ?? DEFAULT_SEPARATOR)}`
							: PROMPT_VARIABLE_TYPE_LABEL[variable.type]}
					</span>
				)}

				{/*
					The separator the PARENT inserts before this part. Shown on the child
					rather than once on the parent, so a reader sees what lands between two
					specific fragments.
				*/}
				{separator === undefined ? null : (
					<span className="text-ds-2xs text-ds-text-subtle">after {describeSeparator(separator)}</span>
				)}
			</div>

			{showChildren ? (
				<div className="relative pl-4">
					{/*
						The vertical rule spanning this parent's children, stopped short on the
						last branch so the corner closes rather than trailing past it.
					*/}
					<span
						aria-hidden
						className="absolute left-1 top-0 w-px bg-ds-border"
						style={{ bottom: isLast ? '0.6rem' : 0 }}
					/>
					{parts.map((part, index) => (
						<div key={part} className="relative">
							<span aria-hidden className="absolute left-1 top-[0.6rem] h-px w-2 bg-ds-border" />
							<div className="pl-3">
								<Node
									nodeKey={part}
									byKey={byKey}
									seen={new Set(seen).add(nodeKey)}
									isLast={index === parts.length - 1}
									{...(index === 0 ? {} : { separator: variable?.separator ?? DEFAULT_SEPARATOR })}
								/>
							</div>
						</div>
					))}
				</div>
			) : null}
		</div>
	)
}

/**
 * A separator as something a reader can see.
 *
 * `", "` and `" "` are indistinguishable when rendered literally, and the difference
 * between them is exactly what a nested join exists to express — so whitespace is
 * named rather than shown.
 */
function describeSeparator(separator: string): string {
	if (separator === ' ') return 'a space'
	if (separator === ', ') return 'a comma'
	if (separator.trim() === '') return `${separator.length} space${separator.length === 1 ? '' : 's'}`
	return `"${separator}"`
}
